import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobalOutlined, SearchOutlined, CloseOutlined, MenuOutlined } from '@ant-design/icons';
import logoImg from '../icon/logo1.svg';

const Navbar: React.FC = () => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchExpanded(false);
      setIsMobileMenuOpen(false);
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsMobileMenuOpen(false);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'zh' : 'en';
    i18n.changeLanguage(newLang);
  };

  return (
    <header className="fixed w-full top-0 z-50 bg-white">
      <nav className="mx-auto px-4 md:px-12 max-w-[2000px] py-8 flex items-center font-['PingFang']">
        {/* Logo - Now links to home */}
        <Link to="/" className="flex-shrink-0">
          <img 
            src={logoImg} 
            alt="东大国际"
            className="h-[40px] w-auto object-contain"
          />
        </Link>

        {/* Mobile Controls */}
        <div className="ml-auto flex items-center md:hidden">
          {/* Mobile Language Switcher */}
          <button
            className="text-gray-500 hover:text-gray-700 mr-6"
            onClick={toggleLanguage}
          >
            <GlobalOutlined style={{ fontSize: '20px' }} />
          </button>
          
          {/* Mobile Menu Button */}
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <MenuOutlined style={{ fontSize: '20px' }} />
          </button>
        </div>

        {/* Right Section: Nav Links, Search & Language */}
        <div className={`
          md:flex md:items-center md:ml-auto md:space-x-8
          ${isMobileMenuOpen 
            ? 'absolute top-full left-0 right-0 flex flex-col bg-white shadow-lg border-t' 
            : 'hidden'
          }
        `}>
          {/* Navigation Links */}
          <div className={`
            md:flex md:items-center md:space-x-12 md:mr-0
            flex flex-col md:flex-row md:space-y-0 md:p-0 mx-5 md:mx-0
            ${isSearchExpanded ? 'md:mr-4' : ''}
          `}>
            <Link 
              to="/projects" 
              className={`text-[20px] md:text-[16px] py-2 md:py-2 h-16 md:h-auto flex items-center pl-[18px] md:pl-0 relative group border-b md:border-b-0 border-gray-100 ${
                location.pathname.startsWith('/projects') ? 'text-black' : 'text-gray-500 hover:text-gray-900'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {i18n.language === 'en' ? 'PROJECTS' : '项目展示'}
              <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-black transition-transform duration-200 hidden md:block ${
                location.pathname.startsWith('/projects') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`} />
            </Link>
            <Link 
              to="/about" 
              className={`text-[20px] md:text-[16px] py-2 md:py-2 h-16 md:h-auto flex items-center pl-[18px] md:pl-0 relative group border-b md:border-b-0 border-gray-100 ${
                location.pathname === '/about' ? 'text-black' : 'text-gray-500 hover:text-gray-900'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {i18n.language === 'en' ? 'ABOUT US' : '关于我们'}
              <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-black transition-transform duration-200 hidden md:block ${
                location.pathname === '/about' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`} />
            </Link>
            <Link 
              to="/join" 
              className={`text-[20px] md:text-[16px] py-2 md:py-2 h-16 md:h-auto flex items-center pl-[18px] md:pl-0 relative group border-b md:border-b-0 border-gray-100 ${
                location.pathname === '/join' ? 'text-black' : 'text-gray-500 hover:text-gray-900'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {i18n.language === 'en' ? 'JOIN US' : '加入我们'}
              <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-black transition-transform duration-200 hidden md:block ${
                location.pathname === '/join' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`} />
            </Link>
            <Link 
              to="/contact" 
              className={`text-[20px] md:text-[16px] py-2 md:py-2 h-16 md:h-auto flex items-center pl-[18px] md:pl-0 relative group border-b md:border-b-0 border-gray-100 ${
                location.pathname === '/contact' ? 'text-black' : 'text-gray-500 hover:text-gray-900'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {i18n.language === 'en' ? 'CONTACT' : '联系我们'}
              <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-black transition-transform duration-200 hidden md:block ${
                location.pathname === '/contact' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`} />
            </Link>
          </div>

          {/* Search */}
          <div className="relative flex items-center px-5 py-4 md:px-0 md:py-0">
            <form onSubmit={handleSearch} className={`flex items-center bg-gray-50 rounded-md overflow-hidden transition-all duration-300 ${
              isSearchExpanded || isMobileMenuOpen ? 'w-full md:w-48' : 'w-8'
            }`}>
              <button 
                type="button"
                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <SearchOutlined style={{ fontSize: '16px' }} />
              </button>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full bg-transparent text-sm border-none focus:outline-none px-2 ${
                  isSearchExpanded || isMobileMenuOpen ? 'opacity-100' : 'opacity-0 w-0'
                }`}
                placeholder={i18n.language === 'en' ? 'Search...' : '搜索...'}
              />
              {(isSearchExpanded || isMobileMenuOpen) && (
                <button 
                  type="button"
                  className="p-2 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setIsSearchExpanded(false);
                    setSearchQuery('');
                  }}
                >
                  <CloseOutlined style={{ fontSize: '14px' }} />
                </button>
              )}
            </form>
          </div>

          {/* Desktop Language Switcher - hidden on mobile */}
          <div className="hidden md:flex items-center space-x-2 px-0 pb-0">
            <GlobalOutlined className="text-gray-400" />
            <button 
              onClick={() => changeLanguage('zh')} 
              className={`text-sm transition-all duration-200 ${
                i18n.language === 'zh' ? 'font-bold text-black' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              中文
            </button>
            <span className="text-gray-300">/</span>
            <button 
              onClick={() => changeLanguage('en')} 
              className={`text-sm transition-all duration-200 ${
                i18n.language === 'en' ? 'font-bold text-black' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              EN
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;